import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Search, Building2 } from "lucide-react";

/**
 * Dialog to pick a client then navigate to /clienti/:id?action=<action>
 * action ∈ 'new-contract' | 'new-pv'
 */
export default function PickClientDialog({ open, onOpenChange, action }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setQ("");
      setRows([]);
      return;
    }
    let alive = true;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/clients", { params: { q: q || undefined } });
        if (alive) setRows((data || []).slice(0, 30));
      } finally {
        if (alive) setLoading(false);
      }
    }, 200);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [q, open]);

  const pick = (c) => {
    onOpenChange(false);
    nav(`/clienti/${c.id}?action=${action}`);
  };

  const label = action === "new-contract" ? "Contract" : "Proces Verbal";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {label} nou · alege clientul
          </DialogTitle>
          <DialogDescription>
            Caută clientul după denumire sau CUI, apoi apasă pentru a deschide formularul.
          </DialogDescription>
        </DialogHeader>

        <div className="relative mb-3">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Denumire, CUI..."
            className="pl-9 h-11"
            data-testid="pick-client-search"
          />
        </div>

        <div className="max-h-[50vh] overflow-y-auto border border-border rounded-md divide-y divide-border">
          {loading ? (
            <p className="text-sm text-muted-foreground text-center py-8">Se încarcă...</p>
          ) : rows.length === 0 ? (
            <div className="text-center py-8">
              <Building2 className="w-6 h-6 mx-auto text-muted-foreground" />
              <p className="text-sm text-muted-foreground mt-2">Niciun client găsit</p>
            </div>
          ) : (
            rows.map((c) => (
              <button
                key={c.id}
                onClick={() => pick(c)}
                className="w-full flex items-center justify-between text-left px-4 py-3 hover:bg-gold-soft/50 transition-colors"
                data-testid={`pick-client-${c.id}`}
              >
                <div className="min-w-0">
                  <p className="font-medium truncate">{c.denumire}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    CUI {c.cui} {c.localitate ? `· ${c.localitate}` : ""}
                  </p>
                </div>
                <span className="text-xs text-gold-deep font-medium ml-3 whitespace-nowrap">Alege →</span>
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
